import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import WorldMap from "../Components/WorldMap";
import "../css/about-scoped.css";

// Icons
import { 
  FaUsers, 
  FaRocket, 
  FaShieldAlt, 
  FaHeart,
  FaTrophy,
  FaGlobeAmericas,
  FaHandshake,
  FaBolt,
  FaAward
} from "react-icons/fa";

const About = () => {
  const stats = [
    { number: "5000+", label: "Happy Customers" },
    { number: "99.9%", label: "Uptime Guarantee" },
    { number: "24/7", label: "Support Available" },
    { number: "4", label: "Data Centers" }
  ];

  const values = [
    {
      icon: FaHeart,
      title: "Customer First",
      description: "We treat our customers like family, putting their success at the heart of everything we do."
    },
    {
      icon: FaShieldAlt,
      title: "Reliability",
      description: "Rock-solid infrastructure and 99.9% uptime guarantee ensure your website is always online."
    },
    {
      icon: FaRocket,
      title: "Innovation",
      description: "We continuously evolve our technology to provide cutting-edge hosting solutions."
    },
    {
      icon: FaHandshake,
      title: "Trust",
      description: "Built on transparency and honesty, we've earned the trust of thousands of customers."
    }
  ];

  const timeline = [
    {
      year: "2020",
      title: "ClubHosty Founded",
      description: [
        "Started with a vision to provide reliable and affordable web hosting solutions for everyone.",
        "Launched with basic web hosting plans and 24/7 customer support."
      ]
    },
    {
      year: "2021",
      title: "First Major Milestones",
      description: [
        "Reached 1000 customer milestone with exceptional support and reliability.",
        "Web Hosting plans were enhanced with SSD drives for better performance."
      ]
    },
    {
      year: "2022",
      title: "Industry Recognition",
      description: [
        "Received 5-star ratings and recognition as one of India's fastest-growing hosting providers.",
        "Formed strategic partnerships to enhance our hosting infrastructure."
      ]
    },
    {
      year: "2023",
      title: "Global Expansion",
      description: [
        "Expanded to 4 data centers across India, Singapore, USA, and Germany.",
        "Enhanced security measures and 99.9% uptime guarantee implementation."
      ]
    },
    {
      year: "2024",
      title: "Technology Innovation",
      description: [
        "Upgraded infrastructure with faster servers and improved performance for all customers.",
        "Introduced advanced monitoring and automated backup solutions."
      ]
    }
  ];

  return (
    <div className="about-page-wrapper">
      <Helmet>
        <title>About ClubHosty - Leading Web Hosting Provider in India</title>
        <meta
          name="description"
          content="Learn about ClubHosty's journey, values, and commitment to providing exceptional web hosting services since 2020. Trusted by 5000+ customers worldwide."
        />
        <link rel="canonical" href="https://www.clubhosty.com/about" />
      </Helmet>

      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <FaTrophy />
              <span>India's Trusted Hosting Provider</span>
            </div>
            <h1 className="hero-title">
              Empowering Digital Dreams Since <span className="text-gradient">2020</span>
            </h1>
            <p className="hero-description">
              We're more than just a hosting company. We're your digital partners, 
              committed to providing reliable, fast, and secure web hosting solutions 
              that help businesses thrive online.
            </p>
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <div key={index} className="stat-item">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <main>
        {/* About Story Section */}
        <section className="about-story">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-5 mb-lg-0">
                <div className="story-content">
                  <div className="story-badge">Our Story</div>
                  <h2 className="story-title">
                    From Humble Beginnings to Industry Leader
                  </h2>
                  <p className="story-description">
                    ClubHosty was born from a simple idea: web hosting shouldn't be complicated 
                    or expensive. Founded in 2020 by a team of passionate developers and hosting 
                    enthusiasts, we set out to democratize web hosting in India.
                  </p>
                  <p className="story-description">
                    What started as a small venture has grown into one of India's most trusted 
                    hosting providers, serving over 5,000 customers worldwide with our reliable 
                    infrastructure and exceptional support.
                  </p>
                  <Link to="/contact" className="btn-cta">
                    <span>Get Started Today</span>
                    <FaRocket />
                  </Link>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="story-image">
                  <img 
                    src="https://res.cloudinary.com/clubhosty/image/upload/v1752978397/generated-image_bfaxwk.png" 
                    alt="ClubHosty Team" 
                    className="img-fluid"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="values-section">
          <div className="container">
            <div className="section-header">
              <div className="section-badge">Our Values</div>
              <h2>What Makes Us Different</h2>
              <p>
                Our core values drive everything we do, from the products we build 
                to the support we provide. These principles guide our commitment 
                to your success.
              </p>
            </div>
            
            <div className="row">
              {values.map((value, index) => (
                <div key={index} className="col-lg-3 col-md-6 mb-4">
                  <div className="value-card">
                    <div className="value-icon">
                      <value.icon />
                    </div>
                    <h4>{value.title}</h4>
                    <p>{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="timeline-section">
          <div className="container">
            <div className="section-header">
              <div className="section-badge">Our Journey</div>
              <h2>Our Companies Timeline</h2>
              <p>
                A brief summary of our companies milestones after more than a decade of being in the hosting industry.
              </p>
            </div>
            
            <div className="timeline">
              {timeline.map((item, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <div className="timeline-year">{item.year}</div>
                    <h4 className="timeline-title">{item.title}</h4>
                    <div className="timeline-description">
                      <ul>
                        {item.description.map((point, pointIndex) => (
                          <li key={pointIndex}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Global Presence Section */}
        <section className="global-section">
          <div className="container">
            <div className="section-header">
              <div className="section-badge">Global Reach</div>
              <h2>Worldwide Infrastructure</h2>
              <p>
                Our strategically located data centers ensure fast loading times 
                and reliable service for your website visitors around the globe.
              </p>
            </div>
            
            <div className="global-map">
              <WorldMap />
              
              {/* Map Legend */}
              <div className="map-legend">
                <div className="legend-item">
                  <div className="legend-marker"></div>
                  <span>ClubHosty Data Centers</span>
                </div>
                <div className="legend-item">
                  <div className="legend-region"></div>
                  <span>Service Regions</span>
                </div>
              </div>
            </div>
            
            <div className="row mt-5">
              <div className="col-lg-3 col-md-6 mb-4">
                <div className="value-card">
                  <div className="value-icon">
                    <FaGlobeAmericas />
                  </div>
                  <h4>4 Data Centers</h4>
                  <p>Strategically located across India, USA, Singapore, and Germany</p>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 mb-4">
                <div className="value-card">
                  <div className="value-icon">
                    <FaBolt />
                  </div>
                  <h4>Lightning Fast</h4>
                  <p>Optimized for speed with SSD storage and CDN integration</p>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 mb-4">
                <div className="value-card">
                  <div className="value-icon">
                    <FaShieldAlt />
                  </div>
                  <h4>Secure & Safe</h4>
                  <p>Advanced security measures and regular backups protect your data</p>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 mb-4">
                <div className="value-card">
                  <div className="value-icon">
                    <FaAward />
                  </div>
                  <h4>Award Winning</h4>
                  <p>Recognized for excellence in hosting services and customer support</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2 className="cta-title">Ready to Join Our Success Story?</h2>
              <p className="cta-description">
                Join thousands of satisfied customers who trust ClubHosty for their 
                web hosting needs. Experience the difference that quality hosting can make.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                <Link to="/shared-hosting" className="btn-cta">
                  <span>View Hosting Plans</span>
                  <FaRocket />
                </Link>
                <Link to="/contact" className="btn-cta">
                  <span>Contact Our Team</span>
                  <FaUsers />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default About;
